import * as React from 'react';
import qs from 'qs';

import {
  ClickoutSegment,
  ClickoutProps,
  Browser,
  PRODUCT_CLICKOUT_PATH,
  CONTENT_CLICKOUT_PATH,
  ClickoutArea,
  Subject,
  Product,
  AlgoliaIndiceName,
} from '@packages/common';

import { useRouter } from 'next/router';

import { ABTestingContext } from '../abTestingContext';
import { CategoryContext } from '../CategoryContext';
import { AppStaticStateContext } from '../AppStaticStateContext';
import { SideEffectsContext } from '../SideEffectsContext';
import { RecommendationChannel } from '../../containers';
import { ProductsContext } from '../ProductsContext';

type ClickoutPathGetterParams = {
  objectID: string;
  area: ClickoutArea;
  index: number;
  sorted?: boolean;
  dy: boolean;
  page: string;
};
type CreateClickoutUrl = (params: ClickoutPathGetterParams) => string;

interface ClickoutContextValue {
  browser: Browser;
  target: HTMLAnchorElement['target'];
  clickoutSubject: Subject<Product | null>;
  segment: ClickoutSegment;
  createClickoutUrl: CreateClickoutUrl;
}

export const ClickoutContext = React.createContext({ clickoutSubject: new Subject(null) } as ClickoutContextValue);

interface ClickoutProviderProps {
  segment: ClickoutSegment;
  recommendationChannel?: RecommendationChannel;
  service?: 'content' | 'product';
}

export const ClickoutProvider: React.FC<ClickoutProviderProps> = ({
  children,
  segment,
  recommendationChannel,
  service = 'product',
}) => {
  const baseUrl = React.useRef(service === 'product' ? PRODUCT_CLICKOUT_PATH : CONTENT_CLICKOUT_PATH);
  const { deviceType, browser } = React.useContext(AppStaticStateContext);
  const { searchTerm } = React.useContext(CategoryContext);
  const { title, config, variant } = React.useContext(ABTestingContext);
  const clickoutSubjectRef = React.useRef(new Subject(null as Product | null));

  const locallyFiltered = false; // TODO: replace it with actual value from Products Search Context
  const sortingIndexName = config?.SORTING_INDICE ?? 'default';

  const createClickoutUrl: CreateClickoutUrl = React.useCallback(
    ({ objectID, area, index, sorted, dy, page }) => {
      const paramsRaw = {
        s: segment,
        d: deviceType,
        c: recommendationChannel,
        // TODO: Remove this when discovery is ready
        ab: title,
        ab_title: title,
        ab_variant: variant,
        l: locallyFiltered,
        t: sortingIndexName,
        page,
        search_term: searchTerm,
        id: objectID,
        a: area,
        sort: sorted,
        p: index,
        dy: dy || null,
      };

      const params = qs.stringify(
        Object.keys(paramsRaw).reduce((acc, paramKey) => {
          const paramValue = paramsRaw[paramKey];
          if (paramValue == null || paramValue === '') return { ...acc };
          return { ...acc, [paramKey]: paramValue };
        }, {}),
      );

      return `${baseUrl.current}?${params}`;
    },
    [deviceType, locallyFiltered, recommendationChannel, searchTerm, segment, sortingIndexName, title, variant],
  );

  const target = browser === 'Facebook' ? '' : '_blank';

  const value = React.useRef({
    browser,
    deviceType,
    target,
    clickoutSubject: clickoutSubjectRef.current,
    createClickoutUrl,
    segment,
  });
  return <ClickoutContext.Provider value={value.current}>{children}</ClickoutContext.Provider>;
};

export const Clickout: React.FC<ClickoutProps> = ({
  children,
  product,
  sorted,
  index,
  area = ClickoutArea.OTHER,
  trackingToken = '',
}) => {
  const { createClickoutUrl, clickoutSubject, segment, target } = React.useContext(ClickoutContext);
  const sideEffectsDispatcher = React.useContext(SideEffectsContext);
  const { productSearchResponse } = React.useContext(ProductsContext);
  const { asPath: page } = useRouter();

  const clickoutUrl = createClickoutUrl({
    objectID: product.objectID,
    area,
    index,
    sorted,
    dy: !!product.isDYRecommended,
    page,
  });

  const handleClick = () => {
    sideEffectsDispatcher.productClickout({
      product,
      segment,
      trackingToken,
      queryID: productSearchResponse?.queryID,
      indexName: productSearchResponse?.indexUsed || AlgoliaIndiceName.PRODUCTS,
      index,
    });
    clickoutSubject.next(product);
  };

  return (
    <a href={clickoutUrl} onClick={handleClick} target={target} rel="noopener nofollow">
      {children}
    </a>
  );
};
